import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  WorkHistory as WorkIcon,
  EmojiEvents as ComisionIcon,
  RequestPage as SolicitudIcon,
  Paid as PaidIcon,
  HourglassTop as PendienteIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { formatDateShort } from "../../utils/dateUtils";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const ANIOS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

const fmt = (n) =>
  Number(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Tarjeta KPI ──────────────────────────────────────────────────────────────
// ─── Tarjeta KPI circular ────────────────────────────────────────────────
const KpiCard = ({ title, value, subtitle, icon, color, noPrefix }) => (
  <Box
    sx={{
      width: { xs: 130, sm: 158 },
      height: { xs: 130, sm: 158 },
      borderRadius: "50%",
      background: `radial-gradient(circle at 35% 35%, ${color}40, ${color}15)`,
      border: `2px solid ${color}50`,
      boxShadow: `0 0 24px ${color}30`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 0.2, sm: 0.35 },
      mx: "auto",
      transition: "transform .2s, box-shadow .2s",
      "&:hover": { transform: "scale(1.06)", boxShadow: `0 0 34px ${color}50` },
    }}
  >
    <Box sx={{ color, lineHeight: 1, "& .MuiSvgIcon-root": { fontSize: { xs: 22, sm: 26 } } }}>
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.2rem" }, color: "#fff", lineHeight: 1, textAlign: "center", px: 0.5 }}>
      {noPrefix ? value : `$${fmt(value)}`}
    </Typography>
    <Typography sx={{ fontSize: { xs: "0.5rem", sm: "0.58rem" }, color: "rgba(255,255,255,0.7)", fontWeight: 600, textAlign: "center", lineHeight: 1.2, px: 1 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: { xs: "0.42rem", sm: "0.5rem" }, color: "rgba(255,255,255,0.45)", textAlign: "center", lineHeight: 1.2, px: 1 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ─── Chip de estado de comisión ───────────────────────────────────────────────
const EstadoComisionChip = ({ estado }) => {
  const map = {
    pendiente:  { label: "Disponible",  color: "#FFC107", icon: <PendienteIcon sx={{ fontSize: 13 }} /> },
    solicitado: { label: "Solicitado",  color: "#2196F3", icon: <SolicitudIcon sx={{ fontSize: 13 }} /> },
    pagado:     { label: "Pagado",      color: "#4CAF50", icon: <PaidIcon sx={{ fontSize: 13 }} /> },
  };
  const s = map[estado] || { label: estado, color: "#9E9E9E" };
  return (
    <Chip
      label={s.label}
      size="small"
      icon={s.icon}
      sx={{ bgcolor: `${s.color}20`, color: s.color, fontWeight: 600, fontSize: 11 }}
    />
  );
};

// ─── Chip estado evento ───────────────────────────────────────────────────────
const EstadoEventoChip = ({ estado }) => {
  const colorMap = {
    Completado:     "#4CAF50",
    "Día Laborado": "#4CAF50",
    Confirmado:     "#2196F3",
    "En curso":   "#9C27B0",
    Pendiente:      "#FFC107",
    Cancelado:      "#F44336",
  };
  const c = colorMap[estado] || "#9E9E9E";
  return (
    <Chip label={estado} size="small"
      sx={{ bgcolor: `${c}20`, color: c, fontWeight: 600, fontSize: 11 }} />
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const ColaboradorFinanzas = () => {
  const theme  = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [periodo, setPeriodo] = useState("mensual");

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [soliciting, setSoliciting] = useState(null); // id de la comisión que se está solicitando
  const [successMsg, setSuccessMsg] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/colaborador/finanzas", {
        params: { periodo, year, month },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error al cargar finanzas");
    } finally {
      setLoading(false);
    }
  }, [periodo, year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refrescar al volver a la pestaña / ventana
  useEffect(() => {
    const onFocus = () => fetchData();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchData]);

  // Solicitar tramo 25%
  const handleSolicitar = async (comisionId) => {
    setSoliciting(comisionId);
    setSuccessMsg("");
    setError(null);
    try {
      await api.post("/colaborador/finanzas/solicitar", { comision_id: comisionId });
      setSuccessMsg("¡Solicitud enviada correctamente! El administrador revisará tu solicitud.");
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar solicitud");
    } finally {
      setSoliciting(null);
    }
  };

  const resumen          = data?.resumen         ?? {};
  const comisionesPorEvento = data?.comisiones_por_evento ?? [];
  const diasLaborados    = data?.dias_laborados   ?? [];
  const config           = data?.config           ?? {};

  return (
    <Box>
      {/* ── Cabecera ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Mis Finanzas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comisiones por evento generado · Días laborados
          </Typography>
        </Box>

        {/* Filtros */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 115 }}>
            <InputLabel>Período</InputLabel>
            <Select value={periodo} label="Período" onChange={(e) => setPeriodo(e.target.value)}>
              <MenuItem value="mensual">Mensual</MenuItem>
              <MenuItem value="anual">Anual</MenuItem>
            </Select>
          </FormControl>
          {periodo === "mensual" && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Mes</InputLabel>
              <Select value={month} label="Mes" onChange={(e) => setMonth(e.target.value)}>
                {MESES.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 95 }}>
            <InputLabel>Año</InputLabel>
            <Select value={year} label="Año" onChange={(e) => setYear(e.target.value)}>
              {ANIOS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* ── Mensajes ── */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {/* ── KPIs circulares ── */}
          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: 4 }}>
            {[
              { title: "Por Cobrar",        value: resumen.total_pendiente_cobrar ?? 0, subtitle: "Pendiente + solicitado", icon: <PendienteIcon />, color: "#FFC107" },
              { title: "Ya Cobrado",         value: resumen.total_ya_cobrado ?? 0,        subtitle: "Pagado en el período",   icon: <PaidIcon />,      color: "#4CAF50" },
              { title: "Eventos Generados",  value: resumen.eventos_generados ?? 0,       subtitle: `Comisión: ${config.porcentaje_comision ?? 10}% del servicio`, icon: <ComisionIcon />, color: "#C77DFF", noPrefix: true },
              { title: "Días Laborados",     value: resumen.dias_laborados ?? 0,          subtitle: resumen.monto_dias_laborados > 0 ? `$${fmt(resumen.monto_dias_laborados)}` : "Monto por definir", icon: <WorkIcon />, color: "#00C9FF", noPrefix: true },
            ].map((kpi) => (
              <Grid item xs={6} sm="auto" key={kpi.title} sx={{ display: "flex", justifyContent: "center" }}>
                <KpiCard {...kpi} />
              </Grid>
            ))}
          </Grid>

          {/* ══════════════════════════════════════════════════════════════════
              CONCEPTO 1: COMISIONES POR EVENTO GENERADO
          ══════════════════════════════════════════════════════════════════ */}
          <Paper
            sx={{
              mb: 4,
              background: "rgba(19,24,41,0.9)",
              border: "1px solid rgba(199,125,255,0.25)",
              borderRadius: 2.5,
              overflow: "hidden",
            }}
          >
            {/* Header del concepto */}
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: "rgba(199,125,255,0.08)",
                borderBottom: "1px solid rgba(199,125,255,0.15)",
              }}
            >
              <Avatar sx={{ bgcolor: "#C77DFF20", color: "#C77DFF", width: 40, height: 40 }}>
                <ComisionIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#C77DFF" }}>
                  Comisión por Evento Generado
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  25% disponible al Confirmar (solicitable) · 75% disponible al Cubrir el evento
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">Solicitado</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#2196F3" }}>
                  ${fmt(resumen.comision_25_solicitado)}
                </Typography>
              </Box>
            </Box>

            {comisionesPorEvento.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No tienes comisiones de eventos generados en este período
                </Typography>
              </Box>
            ) : isMobile ? (
              /* ── Vista móvil comisiones ── */
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {comisionesPorEvento.map((ev) => (
                  <Card
                    key={ev.evento_id}
                    sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 2 }}
                  >
                    <CardContent sx={{ p: "14px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {ev.evento_titulo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ev.fecha_evento ? formatDateShort(ev.fecha_evento) : "—"}
                          </Typography>
                        </Box>
                        <EstadoEventoChip estado={ev.evento_estado} />
                      </Box>

                      {/* Tramo 25% */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1.5,
                          background: "rgba(255,193,7,0.06)",
                          border: "1px solid rgba(255,193,7,0.15)",
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="#FFC107" sx={{ fontWeight: 700 }}>
                            25% — Tramo Confirmación
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {ev.tramo_25 ? `$${fmt(ev.tramo_25.monto)}` : "—"}
                          </Typography>
                        </Box>
                        {ev.tramo_25 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                            <EstadoComisionChip estado={ev.tramo_25.estado} />
                            {ev.tramo_25.estado === "pendiente" && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={soliciting === ev.tramo_25.id}
                                onClick={() => handleSolicitar(ev.tramo_25.id)}
                                sx={{
                                  fontSize: 11,
                                  py: 0.3,
                                  bgcolor: "#FFC107",
                                  color: "#000",
                                  "&:hover": { bgcolor: "#FFB300" },
                                }}
                              >
                                {soliciting === ev.tramo_25.id ? "…" : "Solicitar"}
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">No disponible aún</Typography>
                        )}
                      </Box>

                      {/* Tramo 75% */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          borderRadius: 1.5,
                          background: "rgba(76,175,80,0.06)",
                          border: "1px solid rgba(76,175,80,0.15)",
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="#4CAF50" sx={{ fontWeight: 700 }}>
                            75% — Tramo Cubierto
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {ev.tramo_75 ? `$${fmt(ev.tramo_75.monto)}` : "—"}
                          </Typography>
                        </Box>
                        {ev.tramo_75 ? (
                          <EstadoComisionChip estado={ev.tramo_75.estado} />
                        ) : (
                          <Typography variant="caption" color="text.disabled">Pendiente de cubrir</Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              /* ── Vista desktop comisiones ── */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { color: "text.secondary", fontSize: 12, fontWeight: 700, bgcolor: "rgba(255,255,255,0.03)" } }}>
                      <TableCell>Evento</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Estado Evento</TableCell>
                      <TableCell align="center" sx={{ color: "#FFC107 !important" }}>25% — Confirmación</TableCell>
                      <TableCell align="center" sx={{ color: "#4CAF50 !important" }}>75% — Al Cubrir</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comisionesPorEvento.map((ev) => (
                      <TableRow
                        key={ev.evento_id}
                        sx={{ "&:hover": { background: "rgba(199,125,255,0.04)" }, "& td": { borderColor: "rgba(255,255,255,0.05)" } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ev.evento_titulo}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Servicio: ${fmt(ev.costo_servicio)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {ev.fecha_evento ? formatDateShort(ev.fecha_evento) : "—"}
                        </TableCell>
                        <TableCell><EstadoEventoChip estado={ev.evento_estado} /></TableCell>

                        {/* Tramo 25% */}
                        <TableCell align="center">
                          {ev.tramo_25 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, color: "#FFC107", fontSize: 14 }}>
                                ${fmt(ev.tramo_25.monto)}
                              </Typography>
                              <EstadoComisionChip estado={ev.tramo_25.estado} />
                              {ev.tramo_25.estado === "pendiente" && (
                                <Tooltip title="Solicitar pago del tramo 25%">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={soliciting === ev.tramo_25.id}
                                    onClick={() => handleSolicitar(ev.tramo_25.id)}
                                    sx={{
                                      fontSize: 11,
                                      py: 0.2,
                                      borderColor: "#FFC107",
                                      color: "#FFC107",
                                      "&:hover": { borderColor: "#FFB300", bgcolor: "rgba(255,193,7,0.1)" },
                                    }}
                                  >
                                    {soliciting === ev.tramo_25.id ? "Enviando…" : "Solicitar"}
                                  </Button>
                                </Tooltip>
                              )}
                              {ev.tramo_25.fecha_solicitud && ev.tramo_25.estado === "solicitado" && (
                                <Typography variant="caption" color="text.disabled">
                                  Solicitado: {formatDateShort(ev.tramo_25.fecha_solicitud)}
                                </Typography>
                              )}
                              {ev.tramo_25.fecha_solicitud && ev.tramo_25.estado === "pagado" && (
                                <Typography variant="caption" sx={{ color: "#4CAF50", fontWeight: 600 }}>
                                  Pagado: {formatDateShort(ev.tramo_25.fecha_solicitud)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">No disponible aún</Typography>
                          )}
                        </TableCell>

                        {/* Tramo 75% */}
                        <TableCell align="center">
                          {ev.tramo_75 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, color: "#4CAF50", fontSize: 14 }}>
                                ${fmt(ev.tramo_75.monto)}
                              </Typography>
                              <EstadoComisionChip estado={ev.tramo_75.estado} />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Pendiente de cubrir</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Fila de totales */}
                    <TableRow sx={{ background: "rgba(199,125,255,0.06)", "& td": { borderColor: "rgba(255,255,255,0.1)" } }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTALES</TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 800, color: "#FFC107", fontSize: 13 }}>
                          ${fmt((resumen.comision_25_pendiente ?? 0) + (resumen.comision_25_solicitado ?? 0) + (resumen.comision_25_pagado ?? 0))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pagado: ${fmt(resumen.comision_25_pagado)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 800, color: "#4CAF50", fontSize: 13 }}>
                          ${fmt((resumen.comision_75_pendiente ?? 0) + (resumen.comision_75_pagado ?? 0))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pagado: ${fmt(resumen.comision_75_pagado)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* ══════════════════════════════════════════════════════════════════
              CONCEPTO 2: DÍAS LABORADOS
          ══════════════════════════════════════════════════════════════════ */}
          <Paper
            sx={{
              background: "rgba(19,24,41,0.9)",
              border: "1px solid rgba(0,201,255,0.25)",
              borderRadius: 2.5,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: "rgba(0,201,255,0.06)",
                borderBottom: "1px solid rgba(0,201,255,0.15)",
              }}
            >
              <Avatar sx={{ bgcolor: "#00C9FF20", color: "#00C9FF", width: 40, height: 40 }}>
                <WorkIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#00C9FF" }}>
                  Pago de Día Laborado
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Se habilita cuando el evento que cubriste queda Completado o Día Laborado
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">Por cobrar</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#FFC107" }}>
                  ${fmt(resumen.dias_pendiente)}
                </Typography>
              </Box>
            </Box>

            {diasLaborados.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No tienes días laborados registrados en este período
                </Typography>
              </Box>
            ) : isMobile ? (
              /* ── Vista móvil días laborados ── */
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {diasLaborados.map((d) => (
                  <Card
                    key={d.id}
                    sx={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 2 }}
                  >
                    <CardContent sx={{ p: "12px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.evento_titulo}</Typography>
                        <EstadoComisionChip estado={d.estado} />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {d.fecha_evento ? formatDateShort(d.fecha_evento) : "—"}
                            {d.hora_inicio ? ` · ${d.hora_inicio}` : ""}
                            {d.duracion_horas ? ` · ${d.duracion_horas}h` : ""}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: "#00C9FF", fontSize: 16 }}>
                          {d.monto > 0 ? `$${fmt(d.monto)}` : "Por definir"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              /* ── Vista desktop días laborados ── */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { color: "text.secondary", fontSize: 12, fontWeight: 700, bgcolor: "rgba(255,255,255,0.03)" } }}>
                      <TableCell>Evento Cubierto</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Duración</TableCell>
                      <TableCell>Estado Evento</TableCell>
                      <TableCell align="center">Estado Pago</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diasLaborados.map((d) => (
                      <TableRow
                        key={d.id}
                        sx={{ "&:hover": { background: "rgba(0,201,255,0.04)" }, "& td": { borderColor: "rgba(255,255,255,0.05)" } }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{d.evento_titulo}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {d.fecha_evento ? formatDateShort(d.fecha_evento) : "—"}
                          {d.hora_inicio ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {d.hora_inicio}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {d.duracion_horas ? `${d.duracion_horas} hrs` : "—"}
                        </TableCell>
                        <TableCell><EstadoEventoChip estado={d.evento_estado} /></TableCell>
                        <TableCell align="center"><EstadoComisionChip estado={d.estado} /></TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 700, color: "#00C9FF" }}>
                            {d.monto > 0 ? `$${fmt(d.monto)}` : (
                              <Typography variant="caption" color="text.disabled">Por definir</Typography>
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Fila de total */}
                    <TableRow sx={{ background: "rgba(0,201,255,0.06)", "& td": { borderColor: "rgba(255,255,255,0.1)" } }}>
                      <TableCell colSpan={5} sx={{ fontWeight: 700 }}>TOTAL DÍAS LABORADOS</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 800, color: "#00C9FF", fontSize: 14 }}>
                          ${fmt((resumen.dias_pendiente ?? 0) + (resumen.dias_pagado ?? 0))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pagado: ${fmt(resumen.dias_pagado)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ColaboradorFinanzas;
